import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Input } from '~/components/primitives';
import { fetchWorkOrder, updateWorkOrder } from '~/lib/api/work-orders';
import shared from './_auth._shared.module.css';

/**
 * EFE-4 Pass 90 (2026-05-25) — Work Order edit page.
 * Минимально-достаточный scope для planned/draft state:
 *   - qty_to_produce, planned_start_at, planned_end_at, remarks
 * State guard: edit разрешён только в `planned`/`draft`. После release — read-only
 * (изменения через signal-based transitions).
 */

export const Route = createFileRoute('/_auth/work-orders/$id/edit')({
  component: WoEdit,
});

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function WoEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({ queryKey: ['work-order', id], queryFn: () => fetchWorkOrder(id) });

  const [form, setForm] = useState({
    qty_to_produce: '',
    planned_start_at: '',
    planned_end_at: '',
    remarks: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const w = query.data as Record<string, unknown>;
      setForm({
        qty_to_produce: String(w.qty_to_produce ?? w.qty ?? ''),
        planned_start_at: isoToLocalInput(
          (w.planned_start_at as string | undefined) ?? (w.planned_start as string | undefined),
        ),
        planned_end_at: isoToLocalInput(
          (w.planned_end_at as string | undefined) ?? (w.planned_end as string | undefined),
        ),
        remarks: String((w.remarks as string | undefined) ?? (w.notes as string | undefined) ?? ''),
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    mutationFn: () =>
      updateWorkOrder(id, {
        qty_to_produce: form.qty_to_produce || undefined,
        planned_start_at: localInputToIso(form.planned_start_at),
        planned_end_at: localInputToIso(form.planned_end_at),
        remarks: form.remarks || undefined,
      }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.wo_toast_updated() });
      void qc.invalidateQueries({ queryKey: ['work-order', id] });
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
      void navigate({ to: '/work-orders/$id', params: { id } });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.wo_toast_update_failed(),
        description: err.message,
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;
  const w = query.data as Record<string, unknown>;
  const state = String(w.state ?? w.status ?? 'unknown');
  const editable = state === 'planned' || state === 'draft';
  const voucherNo = String(w.voucher_no ?? id.slice(0, 8));
  if (!editable) {
    return (
      <div>
        <PageHeader
          breadcrumbs={
            <Breadcrumbs
              items={[
                { label: m.nav_work_orders(), to: '/work-orders' },
                { label: voucherNo, to: `/work-orders/${id}` },
                { label: m.common_button_edit() },
              ]}
            />
          }
          title={m.wo_edit_locked_title()}
          description={m.wo_edit_locked_hint({ state })}
        />
        <Button
          variant="secondary"
          onClick={() => navigate({ to: '/work-orders/$id', params: { id } })}
        >
          {m.common_button_back()}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_work_orders(), to: '/work-orders' },
              { label: voucherNo, to: `/work-orders/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={`${m.common_button_edit()}: ${voucherNo}`}
      />
      <div className={shared.formColumn480}>
        <Input
          label={m.wo_field_qty_to_produce()}
          required
          type="number"
          step="0.0001"
          min="0"
          value={form.qty_to_produce}
          onChange={(e) => setForm({ ...form, qty_to_produce: e.target.value })}
        />
        <Input
          label={m.wo_field_planned_start()}
          type="datetime-local"
          value={form.planned_start_at}
          onChange={(e) => setForm({ ...form, planned_start_at: e.target.value })}
        />
        <Input
          label={m.wo_field_planned_end()}
          type="datetime-local"
          value={form.planned_end_at}
          onChange={(e) => setForm({ ...form, planned_end_at: e.target.value })}
        />
        <Input
          label={m.wo_field_remarks()}
          value={form.remarks}
          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/work-orders/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={!form.qty_to_produce}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
